import { useCallback, useContext } from 'react';
import { QueryClient, QueryClientContext, useQuery } from '@tanstack/react-query';
import { insurerRolesService } from '@/services/insurerRoles';
import { useAuthStore } from '@/store';

/**
 * #397: what the signed-in insurer EMPLOYEE's custom role allows.
 *
 * Only `insurer_employee` is gated; every other role passes (admins hold every
 * insurer permission, and non-insurer roles are governed by their own role
 * lists). While the answer loads, or if it failed, gated checks are false --
 * the API refuses the same calls, so a hidden link is never a working one.
 */
const MY_INSURER_PERMISSIONS_KEY = ['insurer-permissions-me'] as const;

let fallbackClient: QueryClient | null = null;
function useResolvedQueryClient(): QueryClient {
  // Same seam as useEmployeeRights: an isolated test render has no provider.
  const contextClient = useContext(QueryClientContext);
  if (contextClient) return contextClient;
  if (!fallbackClient) fallbackClient = new QueryClient();
  return fallbackClient;
}

export function useInsurerPermissions() {
  const { user } = useAuthStore();
  const client = useResolvedQueryClient();
  const gated = user?.role === 'insurer_employee';
  const query = useQuery(
    {
      queryKey: MY_INSURER_PERMISSIONS_KEY,
      queryFn: () => insurerRolesService.me(),
      enabled: gated,
      staleTime: 30_000,
      refetchOnWindowFocus: true,
    },
    client
  );
  const held = query.data?.permissions;
  const can = useCallback((code: string) => (gated ? !!held?.includes(code) : true), [gated, held]);
  return { can, isLoading: gated && query.isLoading, isError: gated && query.isError };
}
